import {
  CircleFragment,
  MemberSummaryFragment,
  RoleSummaryFragment,
} from '../gql'
import { OrgData } from './OrgData'

// Role review (#80): the "Texts" mode of the Projects step. The group says, in
// words, what each person or each role has to report; one text each, carried
// into the meeting summary. Decided with Yohan on 21/09/2026 (note on #80).

export type RoleReviewMode = 'Projects' | 'Texts'
export type RoleReviewBy = 'Member' | 'Role'
export type RoleReviewKind = 'member' | 'role'
export type RoleReviewTextKey = `reviewText:${RoleReviewKind}:${string}`
// Who or what a text is about, frozen when it is written (« Camille
// (Facilitateur·ice, Secrétaire) »): the summary shows the roles as they stood
// in that meeting, and needs no org data to do so.
export type RoleReviewLabelKey = `reviewLabel:${RoleReviewKind}:${string}`

export function roleReviewTextKey(
  kind: RoleReviewKind,
  id: string
): RoleReviewTextKey {
  return `reviewText:${kind}:${id}`
}

export function roleReviewLabelKey(
  kind: RoleReviewKind,
  id: string
): RoleReviewLabelKey {
  return `reviewLabel:${kind}:${id}`
}

export interface RoleReviewEntry {
  label: string
  text: string
}

// What the summary says of a role review: the non-empty texts of each series,
// people in their speaking order, roles in the order they were written in
// (`reviewRolesOrder`), anything else after. A text whose label was never
// frozen (should not happen) is still shown, under an empty label.
export function roleReviewEntries(data: Record<string, unknown>): {
  members: RoleReviewEntry[]
  roles: RoleReviewEntry[]
} {
  const series = (kind: RoleReviewKind, order: unknown) => {
    const prefix = `reviewText:${kind}:`
    const ids = Object.keys(data)
      .filter((key) => key.startsWith(prefix))
      .map((key) => key.slice(prefix.length))
      .filter((id) => {
        const text = data[roleReviewTextKey(kind, id)]
        return typeof text === 'string' && text.trim() !== ''
      })
    const rank = (id: string) => {
      const index = Array.isArray(order) ? order.indexOf(id) : -1
      return index === -1 ? Number.MAX_SAFE_INTEGER : index
    }
    return ids
      .sort((a, b) => rank(a) - rank(b))
      .map((id) => ({
        label: String(data[roleReviewLabelKey(kind, id)] ?? ''),
        text: String(data[roleReviewTextKey(kind, id)]),
      }))
  }
  return {
    members: series('member', data.reviewMembersOrder),
    roles: series('role', data.reviewRolesOrder),
  }
}

export interface RoleReviewRole {
  circle: CircleFragment
  role: RoleSummaryFragment
  holders: MemberSummaryFragment[]
}

export interface RoleReviewMember {
  member: MemberSummaryFragment
  roles: RoleReviewRole[]
}

// The roles under review, in the org chart's order: the meeting circle's
// direct children (the perimeter of every step, `circleWithChildren`) WITHOUT
// the circle itself — its lead speaks for it (Yohan). Held roles first, then
// the vacant ones, which are often exactly what needs saying.
//
// A child's holders are its leaders: its link roles' people for a sub-circle,
// its own people for a plain role (`getLeaders`).
export function roleReviewRoles(
  orgData: OrgData,
  circleId: string
): RoleReviewRole[] {
  const rows: RoleReviewRole[] = []
  for (const circle of orgData.childrenOf(circleId)) {
    const role = orgData.getRole(circle.roleId)
    if (!role) continue
    const holders = uniqueMembers(
      orgData.getLeaders(circle.id).map((leader) => leader.member)
    )
    rows.push({ circle, role, holders })
  }
  return [
    ...rows.filter((row) => row.holders.length > 0),
    ...rows.filter((row) => row.holders.length === 0),
  ]
}

// The people under review: every holder of those roles, present or not, once
// each with all their roles. Ordered as saved (`order`), people no longer
// holding a role dropped, newcomers appended in the org chart's order.
export function roleReviewMembers(
  orgData: OrgData,
  circleId: string,
  order: readonly string[] = []
): RoleReviewMember[] {
  const byId = new Map<string, RoleReviewMember>()
  for (const row of roleReviewRoles(orgData, circleId)) {
    for (const member of row.holders) {
      const entry = byId.get(member.id) ?? { member, roles: [] }
      entry.roles.push(row)
      byId.set(member.id, entry)
    }
  }
  const saved = order.filter((id) => byId.has(id))
  const rest = [...byId.keys()].filter((id) => !saved.includes(id))
  return [...saved, ...rest].map((id) => byId.get(id)!)
}

function uniqueMembers(
  members: MemberSummaryFragment[]
): MemberSummaryFragment[] {
  const seen = new Set<string>()
  return members.filter((m) => !seen.has(m.id) && !!seen.add(m.id))
}
